/**
 * @file useMediaSizes.ts
 * @description Размеры файлов медиатеки для оформления секции (phase19.16,
 * партия SB120, этап 1; uiux-sb120-stage1 §2.1 «1280 ÷ 1,78 = 720 px»,
 * §2.4 «Фото … уже кадра», db-design-sb120-stage1 §4 `sectionLookWarnings`).
 *
 * Откуда размеры, по порядку: карта `media` ответа страницы кабинета
 * (`known`, API `SitePageDetail.media`), выбранный в медиатеке файл
 * (`rememberMediaSize` — точные числа из ответа медиатеки) и, только
 * запасным путём (файла нет в карте или у него нет размеров, старый API),
 * замер картинки браузером по стабильному адресу (`naturalWidth/Height`;
 * картинку и так грузит мини-фото фокуса). Кэш общий на сеанс.
 */

import type { SectionMediaSize, SectionMediaSizes } from "@chaiberi/shared";
import { useEffect, useMemo, useState } from "react";

import { contentMediaUrl } from "../../lib/api-content-media";

/** Размеры по id: известны, неизвестны (`null` поля) или ещё грузятся (нет записи). */
const cache = new Map<string, SectionMediaSize>();
const loading = new Set<string>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

/** Запомнить размеры выбранного файла (ответ медиатеки). */
export function rememberMediaSize(media: { id: string; width: number | null; height: number | null }): void {
  cache.set(media.id, { width: media.width, height: media.height });
  notify();
}

/** Сбросить кэш — только для тестов. */
export function resetMediaSizes(): void {
  cache.clear();
  loading.clear();
}

function measure(id: string): void {
  if (cache.has(id) || loading.has(id) || typeof Image === "undefined") return;
  loading.add(id);
  const img = new Image();
  img.onload = () => {
    loading.delete(id);
    cache.set(id, { width: img.naturalWidth || null, height: img.naturalHeight || null });
    notify();
  };
  img.onerror = () => {
    loading.delete(id);
    cache.set(id, { width: null, height: null });
    notify();
  };
  img.src = contentMediaUrl(id);
}

/** Размеры из карты ответа страницы: только с обоими числами (иначе — запасной замер). */
type KnownSizes = Readonly<Record<string, { width: number | null; height: number | null } | undefined>>;

const hasSize = (size: { width: number | null; height: number | null } | undefined): boolean => !!size?.width && !!size.height;

/**
 * Размеры файлов по списку id (пустые id пропускаются). `known` — карта
 * `media` ответа страницы: из неё — сразу, без замера.
 */
export function useMediaSizes(ids: ReadonlyArray<string | null | undefined>, known: KnownSizes = NO_KNOWN): SectionMediaSizes {
  const [version, setVersion] = useState(0);
  const key = ids.filter((id): id is string => !!id).join("|");
  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    for (const id of key ? key.split("|") : []) if (!hasSize(known[id])) measure(id);
    return () => {
      listeners.delete(listener);
    };
  }, [key, known]);
  return useMemo(() => readSizes(key, known, version), [key, known, version]);
}

const NO_KNOWN: KnownSizes = {};

/**
 * Снимок кэша по ключу списка. `_version` — номер замера: новый номер —
 * новый снимок (кэш изменился), тот же — тот же объект для `useMemo`.
 */
function readSizes(key: string, known: KnownSizes, _version: number): SectionMediaSizes {
  const out: Record<string, SectionMediaSize> = {};
  for (const id of key ? key.split("|") : []) {
    const fromPage = known[id];
    const size = fromPage && hasSize(fromPage) ? { width: fromPage.width, height: fromPage.height } : cache.get(id);
    if (size) out[id] = size;
  }
  return out;
}

/** Пропорции файла (ширина ÷ высота); `null` — неизвестны. */
export function mediaAspect(sizes: SectionMediaSizes, id: string | null | undefined): number | null {
  const size = id ? sizes[id] : undefined;
  return size?.width && size.height ? size.width / size.height : null;
}
